import Link from 'next/link';
import PropTypes from 'prop-types';
import ClosesIn from './ClosesIn';
import { bundleOffer } from '@/lib/homepage/bundles';
import { formatDraw, percentSold, price } from '@/lib/homepage/format';

const DAY = 86400 * 1000;

/**
 * A competition as a raffle ticket: the photo on top, a perforated tear line
 * with notches, then the facts players compare. The same on every screen.
 * `now` is the server render time, used only for the "New" tag. The title
 * link covers the card body and the photo has its own link; the Quick enter
 * button sits above them.
 */
export default function TicketCard({ c, now = 0, priority = false, onQuickEnter, comingSoon = false }) {
  const url = `/competitions/${c.category || 'products'}/${c.slug}`;
  const total = Number(c.totalTickets || 0);
  const left = Math.max(0, total - Number(c.ticketsSold || 0));
  const pct = percentSold(c);
  const closes = c.endDate || c.drawDate;
  const bundle = bundleOffer(c)?.label;
  const isNew = now && c.createdAt && now - new Date(c.createdAt).getTime() < 2 * DAY;
  const tag = comingSoon
    ? 'Coming soon'
    : pct >= 90
      ? 'Last tickets'
      : pct >= 70
        ? 'Selling fast'
        : isNew
          ? 'New'
          : null;

  return (
    <article className="ticket group">
      <div className="ticket-photo">
        <img
          src={c.image}
          alt={c.alt || c.title}
          loading={priority ? 'eager' : 'lazy'}
          className="object-cover transition-transform duration-[600ms] ease-[var(--ease-out)] group-hover:scale-[1.04]"
        />
        {tag && <span className="ticket-tag">{tag}</span>}
        {/* The title link's cover stops at the ticket body (it's positioned
            for the notches), so the photo has its own. Out of the tab order:
            the title link is the one to focus. */}
        <Link href={url} tabIndex={-1} aria-hidden="true" className="absolute inset-0 z-[1]" />
      </div>

      <div className="ticket-body">
        <h3 className="d text-[1.15rem] sm:text-[1.3rem] [font-variation-settings:'wdth'_108] font-extrabold leading-[0.95] line-clamp-3">
          <Link href={url} className="ticket-link">
            {c.title}
          </Link>
        </h3>

        <div className="mt-auto pt-3">
          <div className="flex items-baseline justify-between gap-3">
            <p>
              <span className="num text-[1.9rem] sm:text-[2.1rem] text-accent-bright">{price(c.ticketPrice)}</span>
              <span className="ml-1.5 text-sm text-muted-foreground">a ticket</span>
            </p>
            {bundle && (
              <span className="text-[0.8rem] font-semibold text-foreground/85 whitespace-nowrap">
                {bundle}
              </span>
            )}
          </div>

          {comingSoon ? (
            // Not open yet: when it opens instead of tickets left.
            <p className="mt-2.5 border-t border-white/[0.12] pt-2 flex flex-wrap justify-between gap-x-3 gap-y-0.5 text-[0.82rem] font-medium text-foreground/80 tabular-nums">
              <span className="font-semibold text-accent-bright">Opens {formatDraw(c.startDate)}</span>
              <span>{total.toLocaleString('en-GB')} tickets</span>
            </p>
          ) : (
            <>
              <div className="mt-2.5 h-[3px] w-full bg-white/[0.12]" aria-hidden="true">
                <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-2 flex flex-wrap justify-between gap-x-3 gap-y-0.5 text-[0.82rem] font-medium text-foreground/80 tabular-nums">
                <span>
                  {left.toLocaleString('en-GB')} of {total.toLocaleString('en-GB')} left
                </span>
                <ClosesIn endDate={closes} label={formatDraw(closes)} />
              </p>
            </>
          )}
          {onQuickEnter && !comingSoon && (
            <button
              type="button"
              onClick={() => onQuickEnter(c)}
              className="relative z-[2] mt-3 w-full rounded-[4px] border border-accent/60 py-2 text-sm font-bold text-accent-bright hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              Quick enter
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

TicketCard.propTypes = {
  c: PropTypes.object.isRequired,
  now: PropTypes.number,
  priority: PropTypes.bool,
  onQuickEnter: PropTypes.func,
  comingSoon: PropTypes.bool,
};
